import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { ReportCard, SectionLabel } from '../ReportCard';
import { CreativeCell } from './CreativeCell';
import type { PerformanceReport } from './data/api';
import { compactCurrency, count, percent, spendRatio } from './data/format';

function StatBlock({
  label,
  value,
  caption,
}: {
  label: string;
  value: string;
  caption: string;
}): ReactNode {
  return (
    <ReportCard>
      <SectionLabel>{label}</SectionLabel>
      <Typography sx={{ fontSize: 40, fontWeight: 300, color: 'text.primary' }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{caption}</Typography>
    </ReportCard>
  );
}

export function FatigueSection({
  report,
  onOpenCreative,
}: {
  report: PerformanceReport;
  onOpenCreative: (creativeId: string) => void;
}): ReactNode {
  const { fatigue, projections, currencySymbol } = report;
  const atRisk = projections.filter((row) => row.status === 'at risk');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            xl: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 3,
        }}
      >
        <StatBlock
          label="Spend on fatigued creative"
          value={percent(fatigue.spendShare, 1)}
          caption={`of ${count(fatigue.active)} active creatives' spend`}
        />
        <StatBlock
          label="Fatigued creatives"
          value={count(fatigue.fatigued)}
          caption={`${percent(fatigue.creativeShare, 1)} of active`}
        />
        <StatBlock
          label="Average CTR decline"
          value={percent(fatigue.averageDecline, 1)}
          caption="across creatives fatigued now"
        />
        <StatBlock
          label="Top spenders at risk"
          value={count(atRisk.length)}
          caption="projected to breach the fatigue threshold"
        />
      </Box>

      {projections.length > 0 && (
        <ReportCard label="Days to fatigue">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Creative</TableCell>
                <TableCell align="right">Spend</TableCell>
                <TableCell align="right">Spend ratio</TableCell>
                <TableCell align="right">Projection</TableCell>
                <TableCell align="right">Status</TableCell>
                <TableCell align="right">Counted from</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projections.map((row) => (
                <TableRow key={row.assetId}>
                  <CreativeCell row={row} onOpen={onOpenCreative} />
                  <TableCell align="right">{compactCurrency(row.spend, currencySymbol)}</TableCell>
                  <TableCell align="right">
                    {spendRatio(row.spend, report.periods.current.spend)}
                  </TableCell>
                  <TableCell align="right">
                    {row.daysToFatigue === null
                      ? '—'
                      : row.daysToFatigue === 0
                        ? 'imminent'
                        : `${row.daysToFatigue} days`}
                  </TableCell>
                  <TableCell align="right">{row.status}</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {row.anchoredOn ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ReportCard>
      )}
    </Box>
  );
}
